<!-- signal-result-dialog.component.html -->
<h2 mat-dialog-title class="dialog-header">
  <div class="header-left">
    <mat-icon>analytics</mat-icon>
    <span>Analysis Result</span>
    <span class="job-id-badge">Job ID: {{ data.jobId }}</span>
  </div>
  <div class="header-right">
    <button mat-icon-button (click)="toggleView()" matTooltip="Toggle View">
      <mat-icon>{{ isTableView ? 'bar_chart' : 'table_rows' }}</mat-icon>
    </button>
    <button mat-icon-button (click)="exportData()" matTooltip="Export">
      <mat-icon>file_download</mat-icon>
    </button>
    <button mat-icon-button (click)="refreshData()" matTooltip="Refresh">
      <mat-icon>refresh</mat-icon>
    </button>
  </div>
</h2>

<!-- QuickSight-like Filter Panel -->
<div class="filter-panel" *ngIf="!loading">
  <div class="filter-header">
    <mat-icon>filter_alt</mat-icon>
    <span>Filters</span>
    <button mat-icon-button (click)="clearAllFilters()" *ngIf="hasActiveFilters()">
      <mat-icon>clear_all</mat-icon>
    </button>
  </div>
  <div class="filter-row">
    <mat-form-field appearance="outline" class="filter-field">
      <mat-label>Search Drug</mat-label>
      <input matInput [(ngModel)]="drugFilter" (ngModelChange)="applyFilters()" placeholder="Type drug name...">
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field">
      <mat-label>Search Event</mat-label>
      <input matInput [(ngModel)]="eventFilter" (ngModelChange)="applyFilters()" placeholder="Type event name...">
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field">
      <mat-label>Min Case Count</mat-label>
      <input matInput type="number" [(ngModel)]="minCaseCount" (ngModelChange)="applyFilters()">
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field">
      <mat-label>Min PRR</mat-label>
      <input matInput type="number" step="0.1" [(ngModel)]="minPRR" (ngModelChange)="applyFilters()">
    </mat-form-field>
  </div>
</div>



<!-- Main Content Area -->
<div mat-dialog-content class="dialog-content">

  <!-- Loader -->
  <div *ngIf="loading" class="loader-container">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading analysis results...</p>
  </div>

  <!-- No data message -->
  <div *ngIf="!loading && filteredRowData.length === 0 && originalRowData.length > 0" class="no-data-container">
    <mat-icon>filter_alt_off</mat-icon>
    <p>No results match your filters</p>
    <button mat-button color="primary" (click)="clearAllFilters()">Clear Filters</button>
  </div>

  <div *ngIf="!loading && originalRowData.length === 0" class="no-data-container">
    <mat-icon>insights</mat-icon>
    <p>No results found</p>
  </div>

  <!-- Chart View (QuickSight-like) -->
  <!-- Update the chart-view section in your HTML -->

<!-- Chart View (QuickSight-like) -->
<div *ngIf="!loading && filteredRowData.length > 0 && !isTableView" class="chart-view">
  <div class="chart-grid">
    
    <!-- For single drug: Show metrics comparison -->
    <div class="chart-card" *ngIf="filteredRowData.length === 1">
      <div class="chart-header">
        <span>Signal Strength Metrics for {{ filteredRowData[0].drug_name }} - {{ filteredRowData[0].event_term }}</span>
      </div>
      <div class="simple-chart">
        <div class="metrics-comparison">
          <div *ngFor="let metric of topSignals" class="metric-item">
            <div class="metric-label">{{ metric.metric }}</div>
            <div class="metric-value">{{ metric.value | number:'1.2-2' }}</div>
            <div class="metric-ci" *ngIf="metric.ci_low && metric.ci_high">
              95% CI: {{ metric.ci_low | number:'1.2-2' }} - {{ metric.ci_high | number:'1.2-2' }}
            </div>
            <div class="metric-bar-wrapper">
              <div class="metric-bar-fill" 
                   [style.width.%]="(metric.value / getMaxMetricValue()) * 100">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- For multiple drugs: Show top signals by PRR -->
    <div class="chart-card" *ngIf="filteredRowData.length > 1">
      <div class="chart-header">
        <span>Top Signals by PRR</span>
        <button mat-icon-button (click)="viewChartFullscreen('prr')">
          <mat-icon>open_in_full</mat-icon>
        </button>
      </div>
      <div class="simple-chart">
        <div class="bar-chart">
          <div *ngFor="let signal of topSignals" class="bar-item">
            <div class="bar-label">{{ signal.drug_name }} - {{ signal.event_term | slice:0:20 }}</div>
            <div class="bar-wrapper">
              <div class="bar-fill" [style.width.%]="(signal.prr / (topSignals[0]?.prr || 1)) * 100">
                <span class="bar-value">{{ signal.prr | number:'1.1-1' }}</span>
              </div>
            </div>
            <div class="bar-subtitle">Cases: {{ signal.observed_count }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Case Count Distribution (works for both single and multiple) -->
    <div class="chart-card">
      <div class="chart-header">
        <span>{{ filteredRowData.length === 1 ? 'Case Comparison' : 'Case Count Distribution' }}</span>
      </div>
      <div class="simple-chart">
        <div class="pie-chart-simple">
          <div *ngFor="let item of caseDistribution" class="pie-segment">
            <div class="segment-label">{{ item.range }}</div>
            <div class="segment-bar">
              <div class="segment-fill" 
                   [style.width.%]="(item.count / getTotalForDistribution()) * 100">
              </div>
              <span class="segment-value">{{ item.count | number }} ({{ (item.count / getTotalForDistribution() * 100) | number:'1.0-0' }}%)</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Signal Details Table (mini view) -->
    <div class="chart-card chart-card-wide">
      <div class="chart-header">
        <span>{{ filteredRowData.length === 1 ? 'Detailed Metrics' : 'Top 5 Signals' }}</span>
        <button mat-button (click)="isTableView = true">View Full Table</button>
      </div>
      <ag-grid-angular
        class="ag-theme-balham mini-grid"
        style="width: 100%; height: 250px;"
        [rowData]="filteredRowData.slice(0, 5)"
        [columnDefs]="miniColumnDefs"
        [defaultColDef]="defaultColDef">
      </ag-grid-angular>
    </div>
  </div>
</div>

  <!-- Table View -->
  <div *ngIf="!loading && filteredRowData.length > 0 && isTableView" class="table-view">
    <div class="table-toolbar">
      <mat-form-field appearance="outline" class="search-global">
        <mat-label>Global Search</mat-label>
        <input matInput (keyup)="onQuickFilter($event)" placeholder="Search across all columns...">
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>
      
      <div class="view-options">
        <mat-slide-toggle [(ngModel)]="showPagination">Pagination</mat-slide-toggle>
        <mat-form-field appearance="outline" *ngIf="showPagination">
          <mat-label>Page Size</mat-label>
          <mat-select [(value)]="pageSize" (selectionChange)="onPageSizeChange()">
            <mat-option [value]="10">10 rows</mat-option>
            <mat-option [value]="25">25 rows</mat-option>
            <mat-option [value]="50">50 rows</mat-option>
            <mat-option [value]="100">100 rows</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <ag-grid-angular
      class="ag-theme-balham full-grid"
      style="width: 100%; height: calc(100% - 60px);"
      [rowData]="filteredRowData"
      [columnDefs]="columnDefs"
      [defaultColDef]="defaultColDef"
      [pagination]="showPagination"
      [paginationPageSize]="pageSize"
      [enableBrowserTooltips]="true"
      [enableCellTextSelection]="true"
      (gridReady)="onGridReady($event)">
    </ag-grid-angular>
  </div>

</div>

<div mat-dialog-actions align="end" class="dialog-actions">

  <button mat-raised-button color="primary" mat-dialog-close>Done</button>
</div>